import { createServerFn } from "@tanstack/react-start";

import {
  createDomain,
  createInbox,
  deleteDomain,
  deleteInbox,
  getDomain,
  getDomainOwnership,
  getDomainSetup,
  getDomainSetupStatus,
  getSenderIdentities,
  listDomains,
  listInboxes,
  lookupDnsRecordStatuses,
  updateDomain,
  updateInbox,
} from "@/features/identities/server";
import {
  mapAliasToView,
  mapDomainDnsToView,
  mapDomainToView,
} from "@/features/identities/view-model";

function passthroughValidator<T>(value: T) {
  return value;
}

// While the worker provisions a new domain the setup lists no records yet; the ownership TXT is
// already known and can be published right away, so it is shown on its own.
async function loadDomainDns(domainId: string) {
  const [records, status] = await Promise.all([
    getDomainSetup(domainId),
    getDomainSetupStatus(domainId),
  ]);
  const setup =
    records.dns_records.length > 0
      ? records
      : {
          ...records,
          dns_records: [(await getDomainOwnership(records.domain.domain)).record],
        };
  const liveStatuses = await lookupDnsRecordStatuses(setup.dns_records);

  return { status, dns: mapDomainDnsToView(domainId, setup, status, liveStatuses) };
}

export const getIdentitiesView = createServerFn({ method: "GET" }).handler(async () => {
  const domains = await listDomains();
  return {
    domains: domains.filter((domain) => domain.is_active).map((domain) => mapDomainToView(domain)),
  };
});

export const getDomainAliasesView = createServerFn({ method: "GET" })
  .validator(passthroughValidator<{ domainId: string }>)
  .handler(async ({ data }) => {
    const inboxes = await listInboxes({ domainId: data.domainId });
    return inboxes.filter((inbox) => inbox.is_active).map((inbox) => mapAliasToView(inbox));
  });

export const getDomainDnsView = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ domainId: string; forceRefresh?: boolean }>)
  .handler(async ({ data }) => {
    const { dns } = await loadDomainDns(data.domainId);
    return dns;
  });

// Domain verification is fully worker-driven: the worker re-verifies every pending domain on its
// own sweep and immediately after setup. This server fn only re-reads the current, worker-owned
// status plus a fresh DNS lookup; it never triggers verification.
export const recheckDomainVerification = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ domainId: string }>)
  .handler(async ({ data }) => {
    const [domain, { status, dns }] = await Promise.all([
      getDomain(data.domainId),
      loadDomainDns(data.domainId),
    ]);

    return {
      domain: mapDomainToView(domain, status),
      dns,
    };
  });

export const removeDomain = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ domainId: string }>)
  .handler(async ({ data }) => {
    await deleteDomain(data.domainId);
    return { success: true };
  });

export const saveAlias = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ aliasId: string; signatureHtml: string | null }>)
  .handler(async ({ data }) => {
    return await updateInbox(data.aliasId, { signatureHtml: data.signatureHtml });
  });

export const removeAlias = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ aliasId: string }>)
  .handler(async ({ data }) => {
    await deleteInbox(data.aliasId);
    return { success: true };
  });

export const submitNewDomain = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ domain: string; inboundEnabled: boolean }>)
  .handler(async ({ data }) => {
    const domain = await createDomain({
      domain: data.domain,
      inboundEnabled: data.inboundEnabled,
    });

    return mapDomainToView(domain);
  });

export const updateDomainInbound = createServerFn({ method: "POST" })
  .validator(passthroughValidator<{ domainId: string; inboundEnabled: boolean }>)
  .handler(async ({ data }) => {
    const domain = await updateDomain(data.domainId, { inbound_enabled: data.inboundEnabled });
    const { status, dns } = await loadDomainDns(data.domainId);

    return {
      domain: mapDomainToView(domain, status),
      dns,
    };
  });

export const getVerifiedDomains = createServerFn({ method: "GET" }).handler(async () => {
  const domains = await listDomains();
  return domains
    .filter((domain) => domain.is_active && domain.status === "verified")
    .map((domain) => mapDomainToView(domain));
});

export const getVerifiedIdentityOptions = createServerFn({ method: "GET" }).handler(async () => {
  const senderIdentities = await getSenderIdentities();

  return {
    domains: senderIdentities.domains.map((domain) => ({
      id: domain.id,
      domain: domain.domain,
      status: "verified" as const,
      createdAt: domain.created_at,
      verifiedAt: domain.verified_at,
      dnsProgress: { total: 0, verified: 0 },
      inboundEnabled: true,
    })),
    aliases: senderIdentities.aliases.map((alias) => ({
      id: alias.id,
      alias: alias.alias,
      fullEmail: alias.full_email,
      domainId: alias.domain_id,
      domainName: alias.domain_name,
      signatureHtml: null,
      createdAt: alias.created_at,
    })),
  };
});

export const submitNewAlias = createServerFn({ method: "POST" })
  .validator(
    passthroughValidator<{
      domainId: string;
      alias: string;
      signatureHtml: string | null;
    }>,
  )
  .handler(async ({ data }) => {
    return await createInbox({
      domainId: data.domainId,
      name: data.alias,
      signatureHtml: data.signatureHtml,
    });
  });
